// components/administration/manifests/components/ManifestPassengersTable.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
    Box,
    Badge,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Group as GroupIcon,
    WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import PassengerNotificationHistory from "../../../operations/taxi/PassengerNotificationHistory";
import { getNotificationHistory } from "../../../../api/whatsapp/whatsappService";

const statusColor = (status) => {
    if (status === "CONFIRMADO") return "success";
    if (status === "NO VIAJÓ") return "error";
    return "info";
};

const rowBackground = (status) => {
    if (status === "CONFIRMADO") return "#f1f8e9";
    if (status === "NO VIAJÓ") return "#ffebee";
    return "inherit";
};

// Componente para mostrar el estado de WhatsApp con badge
const PassengerWhatsAppStatus = ({ passengerId, manifestId }) => {
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const checkPending = async () => {
            if (!manifestId || !passengerId) return;

            setLoading(true);
            try {
                const history = await getNotificationHistory(manifestId, passengerId);
                const pending = history.filter(
                    n => n.whanotest === 'PENDIENTE' || n.whanotest === 'FALLIDO'
                );
                setPendingCount(pending.length);
            } catch (error) {
                console.error('Error checking pending notifications:', error);
            } finally {
                setLoading(false);
            }
        };

        checkPending();
    }, [manifestId, passengerId]);

    return (
        <Badge
            badgeContent={pendingCount}
            color="error"
            invisible={pendingCount === 0}
            sx={{
                '& .MuiBadge-badge': {
                    fontSize: 10,
                    height: 18,
                    minWidth: 18,
                    fontWeight: 'bold',
                }
            }}
        >
            <WhatsAppIcon
                color={pendingCount > 0 ? "warning" : "action"}
                sx={{ fontSize: 20 }}
            />
        </Badge>
    );
};

const ManifestPassengersTable = ({
    passengers = [],
    loading = false,
    canSelectPassengers = false,
    onOpenSelector,
    manifestId,
}) => {
    const [selectedPassenger, setSelectedPassenger] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const sortedPassengers = useMemo(() => {
        return [...(Array.isArray(passengers) ? passengers : [])].sort(
            (a, b) =>
                String(a.perape || "").localeCompare(String(b.perape || "")) ||
                String(a.pernom || "").localeCompare(String(b.pernom || ""))
        );
    }, [passengers]);

    const summary = useMemo(() => {
        return sortedPassengers.reduce(
            (acc, p) => {
                acc.total += 1;
                if (p.mandetestpas === "CONFIRMADO") acc.confirmed += 1;
                else if (p.mandetestpas === "NO VIAJÓ") acc.noTravel += 1;
                else acc.reserved += 1;
                if (p.mandettaxser) acc.taxi += 1;
                return acc;
            },
            { total: 0, confirmed: 0, reserved: 0, noTravel: 0, taxi: 0 }
        );
    }, [sortedPassengers]);

    const handleOpenHistory = (passenger) => {
        setSelectedPassenger(passenger);
        setHistoryOpen(true);
    };

    const handleCloseHistory = () => {
        setHistoryOpen(false);
        setSelectedPassenger(null);
    };

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    borderRadius: 2.5,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        backgroundColor: "#fafafa",
                        borderBottom: "1px solid #e0e0e0",
                        flexShrink: 0,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        spacing={1.5}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <GroupIcon color="primary" />
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Pasajeros
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Lista de pasajeros asociados al manifiesto.
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={`${summary.total} total`} />
                            <Chip size="small" color="success" label={`${summary.confirmed} confirmados`} />
                            <Chip size="small" color="info" label={`${summary.reserved} reservados`} />
                            <Chip size="small" color="error" label={`${summary.noTravel} no viajó`} />
                            <Chip size="small" color="warning" label={`${summary.taxi} taxi`} />

                            {canSelectPassengers && (
                                <Button size="small" variant="contained" onClick={onOpenSelector}>
                                    Seleccionar pasajeros
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>#</strong></TableCell>
                                    <TableCell><strong>Pasajero</strong></TableCell>
                                    <TableCell><strong>DNI</strong></TableCell>
                                    <TableCell><strong>Asiento</strong></TableCell>
                                    <TableCell><strong>Subida</strong></TableCell>
                                    <TableCell><strong>Bajada</strong></TableCell>
                                    <TableCell align="center"><strong>Taxi</strong></TableCell>
                                    <TableCell><strong>Estado</strong></TableCell>
                                    <TableCell align="center"><strong>WhatsApp</strong></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {sortedPassengers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            <Typography variant="body2" color="text.secondary" align="center" py={2}>
                                                No hay pasajeros registrados.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedPassengers.map((passenger, index) => (
                                        <TableRow
                                            key={passenger.mandetid || `${passenger.perid}-${index}`}
                                            hover
                                            sx={{ backgroundColor: rowBackground(passenger.mandetestpas) }}
                                        >
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {passenger.perape} {passenger.pernom}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{passenger.perdni || "-"}</TableCell>
                                            <TableCell>{passenger.mandetasi || "N/A"}</TableCell>
                                            <TableCell>{passenger.mandetpunsub || "N/A"}</TableCell>
                                            <TableCell>{passenger.mandetpunbaj || "N/A"}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    size="small"
                                                    label={passenger.mandettaxser ? "Sí" : "No"}
                                                    color={passenger.mandettaxser ? "warning" : "default"}
                                                    variant={passenger.mandettaxser ? "filled" : "outlined"}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={passenger.mandetestpas || "-"}
                                                    color={statusColor(passenger.mandetestpas)}
                                                    sx={{ fontWeight: "bold" }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Ver historial de notificaciones WhatsApp">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenHistory(passenger)}
                                                        disabled={!manifestId}
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: 'success.light',
                                                            }
                                                        }}
                                                    >
                                                        <PassengerWhatsAppStatus
                                                            passengerId={passenger.perid}
                                                            manifestId={manifestId}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Modal de historial de notificaciones */}
            <PassengerNotificationHistory
                manifestId={manifestId}
                passengerId={selectedPassenger?.perid}
                passengerName={`${selectedPassenger?.perape || ''} ${selectedPassenger?.pernom || ''}`}
                phoneNumber={selectedPassenger?.pertel}
                open={historyOpen}
                onClose={handleCloseHistory}
            />
        </>
    );
};

export default ManifestPassengersTable;